import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import colors from '../../assets/theme/colors';
import { CARE_LANGUAGE_LABELS } from '../../constants/config';
import { CARE_PROFILE_ROUTE } from '../../constants/profileRoutes';
import {
  KNOWLEDGE_ITEM_TYPES,
  archiveKnowledgeItem,
  findLikelyKnowledgeConflict,
  findMatchingKnowledgeItem,
  loadKnowledgeItems,
  reinforceKnowledgeItem,
  saveKnowledgeItem,
  saveKnowledgeItemWithSupersession,
} from '../knowledgeItems';
import {
  markCareKnowledgeSourceReviewComplete,
  saveCareKnowledgeAutoSaveProgress,
} from './careSourceActivityService';
import {
  formatCareSourceTimelineDateSummary,
  getCareSourceKnowledgeSuggestions,
  getCareSourceTimelineEntryIds,
} from './careSourceStatusModel';
import {
  buildDraftsFromSuggestions,
  getConfidenceTone,
  groupDraftsByKnowledgeType,
} from './careKnowledgeSuggestionReviewModel';
import ScrollableReviewSection from './ScrollableReviewSection';
import { isComplexDirectProfileKnowledgeSource, isDirectProfileKnowledgeSource } from './directProfileKnowledgeModel';
import KnowledgeConflictConfirmationDialog from './KnowledgeConflictConfirmationDialog';
import {
  buildGenericCareKnowledgeSource,
  buildReviewableCandidatesFromCareSource,
} from './careSourceReviewableCandidates';
import {
  isManualKnowledgeSuggestion,
} from './aiReviewPolicy';
import { getDirectProfileReviewItems } from './DirectProfileOrganizationReview';
import { hasMedicationSourceSuggestions } from './medicationSourceSuggestions';
import { buildCareKnowledgePromotionCandidate } from './careKnowledgePromotionPersistence';
import {
  getCareKnowledgePromotionDecision,
} from './careKnowledgeDurabilityPolicy';
import TimelineCareDetails from './TimelineCareDetails';

const mapReviewTypeToKnowledgeItemType = (knowledgeType = '') => {
  const normalized = String(knowledgeType || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (Object.values(KNOWLEDGE_ITEM_TYPES).includes(normalized)) return normalized;
  return KNOWLEDGE_ITEM_TYPES.OTHER;
};

const CareKnowledgeSuggestionReview = ({
  source = {},
  onError,
  timelinePresentation = false,
  showTimelineDetails = true,
}) => {
  const navigate = useNavigate();
  const genericSource = React.useMemo(() => buildGenericCareKnowledgeSource(source), [source]);
  const sourceSuggestions = genericSource.suggestedCareKnowledgeItems;
  const [drafts, setDrafts] = useState(() => (
    buildDraftsFromSuggestions(getCareSourceKnowledgeSuggestions(genericSource))
  ));
  const [saving, setSaving] = useState(false);
  const [savedItems, setSavedItems] = useState(() => (
    Array.isArray(source.careKnowledgeSavedItems) ? source.careKnowledgeSavedItems : []
  ));
  const [fixOpen, setFixOpen] = useState(false);
  const [destinationSheet, setDestinationSheet] = useState('');
  const [removingId, setRemovingId] = useState('');
  const [showSourceText, setShowSourceText] = useState(false);
  const [pendingConflict, setPendingConflict] = useState(null);
  const [conflictResolutionVersion, setConflictResolutionVersion] = useState(0);
  const autoSaveStartedRef = React.useRef('');
  const conflictDecisionsRef = React.useRef(new Map());

  useEffect(() => {
    setDrafts(buildDraftsFromSuggestions(getCareSourceKnowledgeSuggestions({
      suggestedCareKnowledgeItems: sourceSuggestions,
    })));
    setSaving(false);
    setSavedItems(Array.isArray(source.careKnowledgeSavedItems) ? source.careKnowledgeSavedItems : []);
    setFixOpen(false);
    setDestinationSheet('');
    setShowSourceText(false);
    setPendingConflict(null);
    conflictDecisionsRef.current = new Map();
  }, [source.id, source.careKnowledgeSavedItems, sourceSuggestions]);

  const isReviewed = source.reviewState === 'reviewed' || savedItems.length > 0;
  const isDirectlyPersistedProfileSource = isDirectProfileKnowledgeSource(genericSource);
  const isComplexDirectProfileSource = isComplexDirectProfileKnowledgeSource(genericSource);
  const autoSaveDrafts = React.useMemo(() => (
    isDirectlyPersistedProfileSource && !isComplexDirectProfileSource
      ? []
      : drafts.reduce((items, draft) => {
        const key = draft.content.trim().toLowerCase();
        const promotionDecision = getCareKnowledgePromotionDecision(draft);
        if (
          !draft.selected
          || !draft.title.trim()
          || !key
          || items.some((item) => item.content.trim().toLowerCase() === key)
          || isManualKnowledgeSuggestion(draft)
          || promotionDecision.needsCaregiverDecision
        ) return items;
        return [...items, draft];
      }, [])
  ), [drafts, isComplexDirectProfileSource, isDirectlyPersistedProfileSource]);
  const processingDrafts = React.useMemo(() => (
    isDirectlyPersistedProfileSource && !isComplexDirectProfileSource
      ? []
      : drafts.filter((draft) => (
        draft.selected
        && draft.title.trim()
        && draft.content.trim()
        && !isManualKnowledgeSuggestion(draft)
      ))
  ), [drafts, isComplexDirectProfileSource, isDirectlyPersistedProfileSource]);
  const displayedDrafts = timelinePresentation
    ? processingDrafts
    : autoSaveDrafts;
  const groupedDrafts = React.useMemo(() => (
    groupDraftsByKnowledgeType(savedItems.length ? savedItems : displayedDrafts)
  ), [displayedDrafts, savedItems]);

  const sourceTitle = source.careDocumentTitle
    || source.title
    || source.sourceTitle
    || source.sourceMetadata?.fileName
    || 'Care source';
  const sourceType = source.careDocumentType
    || source.sourceMetadata?.documentTypeLabel
    || source.sourceType
    || 'Source';
  const sourceContext = source.sourceContext
    || source.sourceMetadata?.sourceContext
    || source.metadata?.source?.sourceContext
    || '';
  const timelineEntryIds = React.useMemo(() => (
    getCareSourceTimelineEntryIds(source)
  ), [source]);
  const timelineDateKeys = React.useMemo(() => (
    Array.isArray(source.savedTimelineDateKeys)
      ? source.savedTimelineDateKeys.filter(Boolean)
      : []
  ), [source.savedTimelineDateKeys]);
  const timelineDateSummary = React.useMemo(() => (
    formatCareSourceTimelineDateSummary(source)
  ), [source]);

  useEffect(() => {
    const autoSaveKey = `${source.id || ''}:${autoSaveDrafts.map((draft) => draft.clientId || draft.title).join('|')}`;
    if (!source.id || isReviewed || saving || pendingConflict || savedItems.length || !autoSaveDrafts.length) return;
    if (autoSaveStartedRef.current === autoSaveKey) return;

    autoSaveStartedRef.current = autoSaveKey;
    setSaving(true);
    onError?.('');
    (async () => {
      const savedItems = [];
      const existingItems = await loadKnowledgeItems(source.childId || '');
      for (const [draftIndex, draft] of autoSaveDrafts.entries()) {
        const knowledgeType = mapReviewTypeToKnowledgeItemType(draft.knowledgeType);
        const sourceCollection = sourceContext === 'timeline' ? 'dailyLogs' : 'careSources';
        const itemToSave = buildCareKnowledgePromotionCandidate({
          draft,
          draftIndex,
          knowledgeType,
          source,
          sourceCollection,
          sourceContext,
          sourceTitle,
          sourceType,
          timelineEntryIds,
          timelineDateKeys,
          timelineDateSummary,
        });
        const matchingItem = findMatchingKnowledgeItem(itemToSave, existingItems);
        const promotionDecision = getCareKnowledgePromotionDecision(itemToSave, {
          hasExistingMatch: Boolean(matchingItem),
        });
        if (promotionDecision.action === 'reinforce_existing') {
          const reinforcedItem = await reinforceKnowledgeItem(
            matchingItem,
            itemToSave.sourceRefs[0]
          );
          savedItems.push({
            id: reinforcedItem.id,
            clientId: draft.clientId || reinforcedItem.id,
            knowledgeType: reinforcedItem.knowledgeType || draft.knowledgeType,
            title: reinforcedItem.title || draft.title,
            content: reinforcedItem.summary || draft.content,
            summary: reinforcedItem.summary || draft.content,
            sourceText: reinforcedItem.sourceText || draft.sourceText,
            confidence: draft.confidence || '',
            reinforced: true,
          });
          continue;
        }
        if (promotionDecision.action !== 'auto_promote') continue;
        const conflict = findLikelyKnowledgeConflict(itemToSave, existingItems);
        const decisionKey = draft.clientId || `suggestion:${draftIndex}`;
        const conflictDecision = conflictDecisionsRef.current.get(decisionKey);
        if (conflict && !conflictDecision) {
          setPendingConflict({ conflict, draft, decisionKey });
          autoSaveStartedRef.current = '';
          return;
        }
        const savedItem = conflict && conflictDecision === 'supersede'
          ? await saveKnowledgeItemWithSupersession(itemToSave, conflict.id)
          : await saveKnowledgeItem(itemToSave);
        savedItems.push({
          id: savedItem.id,
          clientId: draft.clientId || savedItem.id,
          knowledgeType: savedItem.knowledgeType || draft.knowledgeType,
          title: savedItem.title || draft.title,
          content: savedItem.summary || draft.content,
          summary: savedItem.summary || draft.content,
          sourceText: savedItem.sourceText || draft.sourceText,
          confidence: draft.confidence || '',
        });
      }

      const hasRemainingManualReview = Boolean(
        buildReviewableCandidatesFromCareSource(source).length
        || getDirectProfileReviewItems(
          source,
          getCareSourceKnowledgeSuggestions(source)
        ).length
        || hasMedicationSourceSuggestions(source)
      );
      if (hasRemainingManualReview) {
        await saveCareKnowledgeAutoSaveProgress(source.id, savedItems);
      } else {
        await markCareKnowledgeSourceReviewComplete(source.id, { savedItems });
      }
      setSavedItems(savedItems);

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('captureez:care-knowledge-refresh', {
          detail: {
            childId: source.childId || null,
            sourceId: source.id || null,
          },
        }));
      }
    })().catch((error) => {
      const rawMessage = error?.message || '';
      const permissionDenied = error?.code === 'permission-denied'
        || /missing or insufficient permissions/i.test(rawMessage);
      onError?.(permissionDenied
        ? "We couldn't save the care notes from this source yet."
        : (rawMessage || "We couldn't save care knowledge right now."));
    }).finally(() => {
      setSaving(false);
    });
  }, [conflictResolutionVersion, isReviewed, onError, pendingConflict, autoSaveDrafts, savedItems.length, saving, source, sourceContext, sourceTitle, sourceType, timelineDateKeys, timelineDateSummary, timelineEntryIds]);

  const resolveConflict = (decision = '') => {
    if (!pendingConflict?.decisionKey || !['keep_both', 'supersede'].includes(decision)) return;
    conflictDecisionsRef.current.set(pendingConflict.decisionKey, decision);
    setPendingConflict(null);
    autoSaveStartedRef.current = '';
    setConflictResolutionVersion((current) => current + 1);
  };

  const handleRemove = async (item = {}) => {
    if (!item.id) return;
    setRemovingId(item.id);
    onError?.('');
    try {
      await archiveKnowledgeItem(item.id);
      setSavedItems((current) => current.filter((savedItem) => savedItem.id !== item.id));
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('captureez:care-knowledge-refresh', {
          detail: {
            childId: source.childId || null,
            sourceId: source.id || null,
          },
        }));
      }
    } catch (error) {
      onError?.(error?.message || "We couldn't remove that care knowledge item right now.");
    } finally {
      setRemovingId('');
    }
  };

  const openDestination = (destination = '') => {
    if (destination === 'care-notes') {
      navigate('/care-knowledge', { state: { childId: source.childId || null } });
    }
    if (destination === 'profile') {
      navigate(CARE_PROFILE_ROUTE, { state: { childId: source.childId || null } });
    }
  };

  if (
    !drafts.length
    || (!displayedDrafts.length && !savedItems.length)
    || (!timelinePresentation && isReviewed && !savedItems.length)
  ) {
    return null;
  }

  const displayedDetailCount = savedItems.length || displayedDrafts.length;
  const originalSourceText = String(
    source.rawText
    || source.originalText
    || source.sourceText
    || autoSaveDrafts.find((draft) => draft.sourceText)?.sourceText
    || ''
  ).trim();
  const knowledgeOwner = source.childName
    ? `${source.childName}’s`
    : 'this person’s';

  if (timelinePresentation) {
    if (!showTimelineDetails) return null;
    return (
      <TimelineCareDetails
        detailCount={displayedDetailCount}
        groupedDrafts={groupedDrafts}
      />
    );
  }

  return (
    <Box sx={{ mx: { xs: -0.2, sm: 0 }, py: { xs: 1, sm: 1.2 } }}>
      <Stack spacing={1}>
        <ScrollableReviewSection
          title="Saved to Care Knowledge"
          listAriaLabel="Automatically saved details"
          borderRadius="22px"
          header={(
            <Box>
              <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.45rem' }, fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.12 }}>
                {`${displayedDetailCount} care detail${displayedDetailCount === 1 ? '' : 's'} saved`}
              </Typography>
              <Typography sx={{ mt: 0.35, fontSize: '0.9rem', color: alpha(colors.brand.deep, 0.72), lineHeight: 1.35 }}>
                {`These details have already been added to ${knowledgeOwner} Care Knowledge.`}
              </Typography>
            </Box>
          )}
        >
          <Stack spacing={1.25}>
            {groupedDrafts.map((group) => (
              <Box key={group.knowledgeType}>
                <Stack spacing={0}>
                  {group.drafts.map((draft, index) => {
                    const showConfidence = String(draft.confidence || '').toLowerCase() !== 'high';

                    return (
                      <Box
                        key={draft.clientId || index}
                        sx={{
                          py: 0.8,
                          borderTop: index === 0 ? `1px solid ${alpha(colors.semantic.ink, 0.08)}` : `1px solid ${alpha(colors.semantic.ink, 0.06)}`,
                        }}
                      >
                        <Stack spacing={0.65}>
                          <Stack direction="row" spacing={0.8} alignItems="flex-start">
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              {index === 0 ? (
                                <Typography sx={{ mb: 0.25, fontSize: '0.82rem', fontWeight: 950, color: colors.semantic.ink }}>
                                  {group.label}
                                </Typography>
                              ) : null}
                              <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: colors.semantic.ink, lineHeight: 1.25 }}>
                                {draft.title}
                              </Typography>
                              <Typography sx={{ mt: 0.2, fontSize: '0.82rem', color: alpha(colors.semantic.ink, 0.66), lineHeight: 1.35 }}>
                                {draft.content || draft.summary}
                              </Typography>
                            </Box>
                            {showConfidence ? (
                              <Chip
                                size="small"
                                label={draft.confidence}
                                sx={{
                                  height: 24,
                                  px: 0.2,
                                  fontSize: '0.68rem',
                                  fontWeight: 900,
                                  textTransform: 'capitalize',
                                  bgcolor: getConfidenceTone(draft.confidence).bg,
                                  color: getConfidenceTone(draft.confidence).color,
                                  border: `1px solid ${getConfidenceTone(draft.confidence).border}`,
                                }}
                              />
                            ) : null}
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        </ScrollableReviewSection>

        <Button
          variant="text"
          onClick={() => setFixOpen(true)}
          disabled={saving || !savedItems.length}
          sx={{
            alignSelf: 'flex-start',
            minHeight: 36,
            px: 0,
            fontSize: '0.9rem',
            fontWeight: 950,
            textTransform: 'none',
            color: colors.brand.deep,
            '&:hover': {
              bgcolor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          {`${CARE_LANGUAGE_LABELS.careKnowledgeReviewSaved} →`}
        </Button>
      </Stack>
      <KnowledgeConflictConfirmationDialog conflict={pendingConflict} onResolve={resolveConflict} />
      <Dialog
        open={fixOpen}
        onClose={() => setFixOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: { xs: '24px 24px 0 0', sm: '24px' },
            m: { xs: 0, sm: 2 },
            alignSelf: { xs: 'flex-end', sm: 'center' },
            width: { xs: '100%', sm: 'calc(100% - 32px)' },
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography sx={{ fontSize: { xs: '1.45rem', sm: '1.7rem' }, fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.08 }}>
                  Review saved care details
                </Typography>
                <Typography sx={{ mt: 0.7, fontSize: '0.95rem', color: alpha(colors.semantic.ink, 0.66), lineHeight: 1.45 }}>
                  These care details were saved from this note. Remove anything that should not remain in Care Knowledge.
                </Typography>
              </Box>
              <IconButton aria-label="Close" onClick={() => setFixOpen(false)}>
                <CloseRoundedIcon />
              </IconButton>
            </Stack>

            {originalSourceText ? (
              <Box>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<Inventory2OutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setShowSourceText((current) => !current)}
                  aria-expanded={showSourceText}
                  sx={{
                    minHeight: 30,
                    px: 0,
                    fontSize: '0.8rem',
                    fontWeight: 850,
                    textTransform: 'none',
                    color: colors.brand.deep,
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  {showSourceText ? 'Hide original note' : 'View original note'}
                </Button>
                <Collapse in={showSourceText} unmountOnExit>
                  <Typography
                    sx={{
                      mt: 0.15,
                      p: 1,
                      borderRadius: '12px',
                      bgcolor: alpha(colors.semantic.ink, 0.04),
                      color: alpha(colors.semantic.ink, 0.72),
                      fontSize: '0.82rem',
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {originalSourceText}
                  </Typography>
                </Collapse>
              </Box>
            ) : null}

            <Box sx={{ borderRadius: '20px', border: `1px solid ${alpha(colors.semantic.line, 0.9)}`, overflow: 'hidden' }}>
              {(savedItems.length ? savedItems : []).map((item, index) => (
                <Stack
                  key={item.id || item.clientId || index}
                  data-testid="saved-care-detail-card"
                  spacing={0.9}
                  sx={{
                    p: 1.25,
                    borderTop: index === 0 ? 'none' : `1px solid ${alpha(colors.semantic.line, 0.72)}`,
                  }}
                >
                  <Box
                    data-testid="saved-care-detail-content-row"
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.2,
                      minWidth: 0,
                      width: '100%',
                    }}
                  >
                    <Box sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '13px',
                      bgcolor: alpha(colors.semantic.sageDeep, 0.13),
                      color: colors.semantic.sageDeep,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box
                      data-testid="saved-care-detail-text-column"
                      style={{ flex: 1, minWidth: 0 }}
                      sx={{ minWidth: 0, flex: 1 }}
                    >
                      <Typography sx={{ fontSize: '0.95rem', fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.2 }}>
                        {item.title}
                      </Typography>
                      <Typography
                        data-testid="saved-care-detail-body"
                        style={{ overflowWrap: 'normal', wordBreak: 'normal' }}
                        sx={{
                          mt: 0.2,
                          fontSize: '0.84rem',
                          color: alpha(colors.semantic.ink, 0.62),
                          lineHeight: 1.35,
                          overflowWrap: 'normal',
                          wordBreak: 'normal',
                        }}
                      >
                        {item.content || item.summary}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={removingId === item.id}
                    onClick={() => handleRemove(item)}
                    sx={{
                      alignSelf: { xs: 'stretch', sm: 'flex-start' },
                      ml: { xs: 0, sm: '50px' },
                      minHeight: 36,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 700,
                      color: '#B42318',
                      borderColor: alpha('#B42318', 0.28),
                      bgcolor: alpha('#B42318', 0.02),
                      '&:hover': {
                        borderColor: alpha('#B42318', 0.42),
                        bgcolor: alpha('#B42318', 0.06),
                      },
                    }}
                  >
                    {removingId === item.id ? 'Removing...' : 'Remove from Care Knowledge'}
                  </Button>
                </Stack>
              ))}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(destinationSheet)}
        onClose={() => setDestinationSheet('')}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: { xs: '24px 24px 0 0', sm: '24px' },
            m: { xs: 0, sm: 2 },
            alignSelf: { xs: 'flex-end', sm: 'center' },
            width: { xs: '100%', sm: 'calc(100% - 32px)' },
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.2, sm: 3 } }}>
          <Stack spacing={1.6}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography sx={{ fontSize: { xs: '1.45rem', sm: '1.7rem' }, fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.08 }}>
                  {destinationSheet === 'profile' ? 'Used in Profile' : `Saved to ${CARE_LANGUAGE_LABELS.careKnowledge}`}
                </Typography>
                <Typography sx={{ mt: 0.7, fontSize: '0.95rem', color: alpha(colors.semantic.ink, 0.66), lineHeight: 1.45 }}>
                  {destinationSheet === 'profile'
                    ? 'These notes can help summarize what matters for ongoing care.'
                    : 'These were saved for later reference, separate from the day-by-day timeline.'}
                </Typography>
              </Box>
              <IconButton aria-label="Close" onClick={() => setDestinationSheet('')}>
                <CloseRoundedIcon />
              </IconButton>
            </Stack>

            <Box sx={{ borderRadius: '18px', border: `1px solid ${alpha(colors.semantic.line, 0.9)}`, overflow: 'hidden' }}>
              {(savedItems.length ? savedItems : autoSaveDrafts).map((item, index) => (
                <Box
                  key={item.id || item.clientId || index}
                  sx={{
                    p: 1.15,
                    borderTop: index === 0 ? 'none' : `1px solid ${alpha(colors.semantic.line, 0.72)}`,
                  }}
                >
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.25 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ mt: 0.2, fontSize: '0.82rem', color: alpha(colors.semantic.ink, 0.64), lineHeight: 1.35 }}>
                    {item.content || item.summary}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Button
              variant="outlined"
              onClick={() => openDestination(destinationSheet)}
              sx={{
                alignSelf: 'flex-start',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 900,
                borderColor: alpha(colors.brand.deep, 0.35),
                color: colors.brand.deep,
              }}
            >
              {destinationSheet === 'profile' ? 'Open Profile' : `Open ${CARE_LANGUAGE_LABELS.careKnowledge}`}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CareKnowledgeSuggestionReview;
